

































































































import {
  defineComponent,
  computed,
  SetupContext,
  reactive,
  watch,
  Ref,
} from '@vue/composition-api'
import moment from 'moment'

import { IMatched } from '@/types/matched'
import { IRecommend } from '@/types/recommend'
import { getRecommends } from '@/api/recommend'
import { createChangedMatched } from '@/api/changed-matcheds'

import { MasterAutocomplete } from '@/components/atoms'
import { LogType, WorkResult } from '@/types/log'
import { IUser } from '@/types/user'
import useVuex from '@/utils/useVuex'
import { UserState } from '@/types/store'

type Props = {
  matcheds: IMatched[]
  loading: boolean
  isCheckeds: boolean
}

export default defineComponent({
  name: 'MatchedsTable',
  components: { MasterAutocomplete },
  props: {
    matcheds: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props: Props, context: SetupContext) {
    const store = context.root.$store

    const state = reactive<{
      user: Ref<IUser | null>
      showCategories: Record<string, boolean>
      categoryIds: Record<string, number>
      recommendLists: Record<string, IRecommend[]>
      recommendInputs: Record<string, string>
      matcheds: Record<string, IMatched | null>
      isCheckeds: Record<string, boolean>
      isAllChecked: boolean
      page: number
      itemsPerPage: number
    }>({
      user: useVuex<UserState['user']>(
        context,
        () => store.getters['user/user'],
      ),
      showCategories: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return { [item.id]: false }
        }),
      ),
      categoryIds: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return { [item.id]: -1 }
        }),
      ),
      recommendLists: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return {
            [item.id]: [],
          }
        }),
      ),
      recommendInputs: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return {
            [item.id]: '',
          }
        }),
      ),
      matcheds: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return { [item.id]: null }
        }),
      ),
      isCheckeds: Object.assign(
        {},
        ...props.matcheds.map(item => {
          return { [item.id]: false }
        }),
      ),
      isAllChecked: false,
      page: 1,
      itemsPerPage: 50,
    })

    type MatchedTableItem = {
      id: string
      createdAt: string
      targetOcr: string | null
      name: string
      code: string
      category: string
      matchedName: string
      isToBeDeleted: boolean
      isApproved: boolean
      score: number
    }

    const items = computed<MatchedTableItem[]>(() =>
      props.matcheds.map(m => {
        return {
          id: m.id,
          createdAt: moment(m.createdAt).format('YYYY/MM/DD HH:mm:ss'),
          targetOcr: m.targetOcr,
          name: m.name,
          code: m.code,
          category: m.category || '-',
          matchedName: m.matchedName,
          isToBeDeleted: m.isToBeDeleted,
          isApproved: m.isApproved,
          score: m._score,
          count: m._count,
        }
      }),
    )

    const headers = [
      { text: '', value: 'isChecked', width: 10 },
      { text: '作成日', value: 'createdAt', width: 120 },
      { text: 'OCR', value: 'targetOcr', width: 180 },
      { text: 'レシート表示名', value: 'name', width: 180 },
      { text: '商品名', value: 'matchedName', width: 180 },
      // { text: '商品コード', value: 'code', width: 100 },
      { text: 'カテゴリー', value: 'category', width: 100 },
      // { text: 'スコア', value: 'score', width: 80 },
      { text: '再紐付', value: 'reMatch', width: 200 },
      { text: '', value: 'reOk', align: 'center', width: 100 },
      {
        text: '削除フラグ',
        value: 'isToBeDeleted',
        align: 'center',
        width: 100,
      },
      {
        text: '承認',
        value: 'isApproved',
        align: 'center',
        width: 100,
      },
      { text: '解除', value: 'delete', align: 'center', width: 100 },
    ]

    // 検索時にcheckedを外す
    watch(
      () => props.loading,
      () => {
        state.isAllChecked = false
        state.isCheckeds = Object.assign(
          {},
          ...props.matcheds.map(item => {
            return { [item.id]: false }
          }),
        )
      },
    )

    // すべて選択
    watch(
      () => state.isAllChecked,
      () => {
        state.isCheckeds = Object.assign(
          {},
          ...props.matcheds.map((item, index) => {
            const toBeChecked =
              (state.page - 1) * state.itemsPerPage <= index &&
              index < state.page * state.itemsPerPage
            return { [item.id]: toBeChecked && state.isAllChecked }
          }),
        )
      },
    )

    const createLog = async (
      user: IUser,
      linkedItems: number,
      workResult: WorkResult,
      note: string,
    ) => {
      const now = moment()
      await store.dispatch('log/createLog', {
        logType: LogType.REMATCH,
        workedById: user.id,
        castId: user.castId,
        castName: user.castName,
        workerType: user.role,
        receiptId: null,
        workedAt: now.toDate(),
        workDuration: 0,
        workTarget: 'reMatched',
        isModified: null,
        workResult,
        approvalResult: null,
        count: 1,
        isBasicInfoModified: false,
        linkedItems,
        isRewarded: user.isRewarded,
        ngReasonFrom: null,
        ngReasonTo: null,
        note,
      })
    }

    const generateNote = (item: MatchedTableItem): string => {
      return `targetOcr=${item.targetOcr}
name=${item.name}
matchedName=${item.matchedName}`
    }

    const updateMatched = async (targetItem: MatchedTableItem) => {
      if (state.user === null) {
        return
      }

      const targetCode = targetItem.code
      const targetIds = Object.keys(state.isCheckeds).filter(
        key => state.isCheckeds[key],
      )
      if (targetIds.indexOf(targetItem.id) === -1) {
        targetIds.push(targetItem.id)
      }
      await Promise.all(
        targetIds.map(async id => {
          const item = items.value.find(i => i.id === id)
          if (item === undefined) {
            return
          }
          const matched: Partial<IMatched> = {
            id: item.id,
            name: item.name,
            code: targetCode,
            category: item.category === '-' ? null : item.category,
            matchedName: item.matchedName,
            targetOcr: item.targetOcr,
            isToBeDeleted: item.isToBeDeleted,
            isApproved: item.isApproved,
          }
          await store.dispatch('matched/updateMatched', {
            id,
            matched,
          })
        }),
      )

      await createLog(
        state.user,
        targetIds.length,
        WorkResult.REMATCH,
        generateNote(targetItem),
      )
    }

    const onToggleIsToBeDeleted = async (targetItem: MatchedTableItem) => {
      if (state.user === null) {
        return
      }

      const targetIsToBeDeleted = !targetItem.isToBeDeleted
      const targetIds = Object.keys(state.isCheckeds).filter(
        key => state.isCheckeds[key],
      )
      if (targetIds.indexOf(targetItem.id) === -1) {
        targetIds.push(targetItem.id)
      }
      await Promise.all(
        targetIds.map(async id => {
          const item = items.value.find(i => i.id === id)
          if (item === undefined) {
            return
          }
          const matched: Partial<IMatched> = {
            id: item.id,
            name: item.name,
            code: item.code,
            category: item.category === '-' ? null : item.category,
            matchedName: item.matchedName,
            targetOcr: item.targetOcr,
            isToBeDeleted: targetIsToBeDeleted,
            isApproved: item.isApproved,
          }
          await store.dispatch('matched/updateMatched', {
            id,
            matched,
          })
          await createChangedMatched(item.id, targetIsToBeDeleted)
        }),
      )

      await createLog(
        state.user,
        targetIds.length,
        WorkResult.TOBEDELETED,
        generateNote(targetItem),
      )
    }

    const onToggleIsApproved = async (targetItem: MatchedTableItem) => {
      if (state.user === null) {
        return
      }

      const targetIsApproved = !targetItem.isApproved
      const targetIds = Object.keys(state.isCheckeds).filter(
        key => state.isCheckeds[key],
      )
      if (targetIds.indexOf(targetItem.id) === -1) {
        targetIds.push(targetItem.id)
      }
      await Promise.all(
        targetIds.map(async id => {
          const item = items.value.find(i => i.id === id)
          if (item === undefined) {
            return
          }
          const matched: Partial<IMatched> = {
            id: item.id,
            name: item.name,
            code: item.code,
            category: item.category === '-' ? null : item.category,
            matchedName: item.matchedName,
            targetOcr: item.targetOcr,
            isToBeDeleted: item.isToBeDeleted,
            isApproved: targetIsApproved,
          }
          await store.dispatch('matched/updateMatched', {
            id,
            matched,
          })
        }),
      )

      await createLog(
        state.user,
        targetIds.length,
        WorkResult.APPROVE,
        generateNote(targetItem),
      )
    }

    const execRecommendSearch = async (q: string, id: string) => {
      // const categoryId = isRestaurant.value ? 2 : state.categoryIds[index]
      const categoryId = state.categoryIds[id]
      state.recommendLists = {
        ...state.recommendLists,
        [id]: await getRecommends(q, categoryId),
      }
    }

    const clearRecommendList = async (id: string) => {
      state.recommendLists = {
        ...state.recommendLists,
        [id]: [],
      }
    }

    const onFocusMasterSelector = (id: string) => {
      state.showCategories = {
        ...state.showCategories,
        [id]: true,
      }
    }

    const onBlurMasterSelector = () => {
      state.showCategories = Object.assign(
        {},
        ...props.matcheds.map(item => {
          return { [item.id]: false }
        }),
      )
    }

    const onSelectRecommend = async (matched: IMatched, id: string) => {
      state.matcheds = {
        ...state.matcheds,
        [id]: matched,
      }
      onBlurMasterSelector()
    }

    const onUnselectRecommend = async (id: string) => {
      state.matcheds = {
        ...state.matcheds,
        [id]: null,
      }
    }

    const isDisableInput = (item: MatchedTableItem): boolean => {
      return state.recommendInputs[item.id]
        ? state.recommendInputs[item.id].length === 0
        : true
    }

    const onClickReOk = async (item: MatchedTableItem) => {
      if (
        !window.confirm(
          '再紐付してよろしいですか？（この操作は取り消しできません）',
        )
      ) {
        return
      }
      const matched = state.matcheds[item.id]
      if (matched === null) {
        return
      }
      {
        item.code = matched.code
        item.matchedName = matched.name
        item.category = matched?.category || '-'
        item.isToBeDeleted = matched.isToBeDeleted
        item.isApproved = matched.isApproved
      }
      await updateMatched(item)
      await createChangedMatched(item.id)
      state.showCategories[item.id] = false
      state.categoryIds[item.id] = -1
      state.recommendLists[item.id] = []
      state.recommendInputs[item.id] = ''
      state.matcheds[item.id] = null
      state.isCheckeds[item.id] = false
    }

    const onClickDelete = async (targetItem: MatchedTableItem) => {
      if (state.user === null) {
        return
      }

      if (
        window.confirm(
          'マッチ済データを解除してよろしいですか？（この操作は取り消しできません）',
        )
      ) {
        const targetIds = Object.keys(state.isCheckeds).filter(
          key => state.isCheckeds[key],
        )
        if (targetIds.indexOf(targetItem.id) === -1) {
          targetIds.push(targetItem.id)
        }
        await Promise.all(
          targetIds.map(async id => {
            const item = items.value.find(i => i.id === id)
            if (item === undefined) {
              return
            }
            await store.dispatch('matched/deleteMatched', id)
          }),
        )

        await createLog(
          state.user,
          targetIds.length,
          WorkResult.DELETE,
          generateNote(targetItem),
        )
      }
    }

    return {
      state,
      headers,
      items,
      onToggleIsToBeDeleted,
      onToggleIsApproved,
      onClickDelete,
      onSelectRecommend,
      onUnselectRecommend,
      onFocusMasterSelector,
      onBlurMasterSelector,
      execRecommendSearch,
      clearRecommendList,
      isDisableInput,
      onClickReOk,
    }
  },
})
